import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  assetMasterService,
  AssetMasterAccessRecord,
} from "../services/assetMasterService";

export interface AssetMasterAccessState {
  loading: boolean;
  error: string | null;
  access: AssetMasterAccessRecord | null;
  /**
   * "Read" access — gates the Assets (my assets) page and Delivery Status.
   * Derived from the `read_access` column (or admin/write as supersets).
   */
  canRead: boolean;
  /**
   * Gates the Asset Master list page (all approved assets). Derived from the
   * `asset_master_access` column, write access, or admin.
   */
  canAssetMaster: boolean;
  canWrite: boolean;
  canDelete: boolean;
  isCustodian: boolean;
  isApprover: boolean;
}

const INITIAL: AssetMasterAccessState = {
  loading: true,
  error: null,
  access: null,
  canRead: false,
  canAssetMaster: false,
  canWrite: false,
  canDelete: false,
  isCustodian: false,
  isApprover: false,
};

const AssetMasterAccessContext = createContext<AssetMasterAccessState>(INITIAL);

export const useAssetMasterAccess = () => useContext(AssetMasterAccessContext);

export const AssetMasterAccessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<AssetMasterAccessState>(INITIAL);

  useEffect(() => {
    let mounted = true;
    const fetchAccess = async () => {
      if (!user?.id) {
        setState((prev) => ({
          ...prev,
          loading: false,
          access: null,
          canRead: false,
          canAssetMaster: false,
          canWrite: false,
          canDelete: false,
          isCustodian: false,
          isApprover: false,
        }));
        return;
      }

      // Admins implicitly get every Asset Master permission. Skip the API
      // fetch entirely so admins never see a loading spinner or an access
      // error screen if the access endpoint is unreachable.
      if (user.isadmin) {
        setState({
          loading: false,
          error: null,
          access: null,
          canRead: true,
          canAssetMaster: true,
          canWrite: true,
          canDelete: true,
          isCustodian: true,
          isApprover: true,
        });
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const access = await assetMasterService.getUserAccess(user.id);
        if (!mounted) return;
        const hasWrite = !!access?.write_access;
        setState({
          loading: false,
          error: null,
          access,
          canRead: !!access?.read_access || hasWrite,
          canAssetMaster: !!access?.asset_master_access || hasWrite,
          canWrite: hasWrite,
          canDelete: !!access?.delete_access,
          isCustodian: !!access?.is_custodian,
          isApprover: !!access?.is_approver,
        });
      } catch (e: any) {
        if (!mounted) return;
        setState({
          loading: false,
          error: e?.message || "Failed to load AssetMaster access",
          access: null,
          canRead: false,
          canAssetMaster: false,
          canWrite: false,
          canDelete: false,
          isCustodian: false,
          isApprover: false,
        });
      }
    };

    fetchAccess();
    return () => {
      mounted = false;
    };
  }, [user?.id, user?.isadmin]);

  const value = useMemo(() => state, [state]);
  return (
    <AssetMasterAccessContext.Provider value={value}>
      {children}
    </AssetMasterAccessContext.Provider>
  );
};
